import React, { useState } from 'react';

import { Box, Flex, Label, Radio, Text } from '@theme-ui/components';

import Button from '../Button/Button';
import HorizontalLine from '../HorizontalLine/HorizontalLine';

import { DEFAULT_STEP_OPTIONS } from '../../constants/onboarding';
import {
  Layer,
  LayerOption as LayerOptionType,
} from '../../providers/SiflorMapProvider/types';
import { Steps } from 'intro.js-react';

type LayerOptions = Array<Layer>;

type LayerOptionProps = {
  value: string;
  label: string;
  onSelect: (value: string) => void;
  selected?: boolean;
  children?: React.ReactNode;
};

const LayerOption = ({
  value,
  label,
  onSelect,
  selected,
  children,
}: LayerOptionProps) => {
  return (
    <Flex sx={{ flexDirection: 'column' }}>
      <Label aria-label={value} sx={{ color: selected ? 'text' : null }}>
        <Radio
          onChange={(e) => onSelect(e.target.value)}
          sx={{ marginRight: 7 }}
          name="layer"
          value={value}
          checked={selected}
        />
        <Box sx={{ display: 'inline-block' }}>{label}</Box>
      </Label>
      <Box sx={{ paddingTop: 4 }}>{children}</Box>
    </Flex>
  );
};

type LayerGroupProps = {
  layer: Layer;
  selected?: LayerOptionType;
  onSelect: (value: string) => void;
};

const LayerGroup = ({ layer, onSelect, selected }: LayerGroupProps) => {
  return (
    <Box sx={{ marginTop: 0 }}>
      <Text
        sx={{
          fontFamily: 'heading',
          fontWeight: 'body',
          marginBottom: 9,
        }}
      >
        {layer.group}
      </Text>
      <Flex
        sx={{
          flexDirection: 'column',
          gap: 7,
          marginTop: 8,
          fontFamily: 'body',
          fontWeight: ' body',
          color: 'neutral',
          paddingLeft: 8,
          fontSize: 3,
        }}
      >
        {layer.options.map((option, idx) => {
          const selectedOption =
            selected?.layerCode === option.code &&
            selected?.groupCode === layer.code;

          return (
            <LayerOption
              key={`${layer.group}-${idx}-${option.code}`}
              value={option.code}
              label={option.name}
              onSelect={onSelect}
              selected={selectedOption}
            />
          );
        })}
      </Flex>
    </Box>
  );
};

export type LayerCategoryProps = {
  layers: LayerOptions;
  onApply: (args: LayerOptionType) => void;
  onCancel: () => void;
  initialLayer?: LayerOptionType;
};

export const ONBOARD_KEY = 'onboarding:LayerCategory';

const LayerCategory = ({
  layers,
  onApply,
  onCancel,
  initialLayer,
}: LayerCategoryProps) => {
  const [selected, setSelected] = useState(initialLayer);

  const [state, setState] = React.useState({
    stepsEnabled: !localStorage.getItem(ONBOARD_KEY),
    initialStep: 0,
    steps: [
      {
        element: '.modal-layers-list',
        intro: 'Selecione uma das camadas que devem ser aplicada no mapa',
      },
    ],
  });

  const onExit = () => {
    setState((prev) => ({ ...prev, stepsEnabled: false }));
    localStorage.setItem(ONBOARD_KEY, 'showed');
  };

  return (
    <Box aria-label="layer-category" sx={{ paddingTop: 8 }}>
      <Steps
        enabled={state.stepsEnabled}
        steps={state.steps}
        initialStep={state.initialStep}
        onExit={onExit}
        options={DEFAULT_STEP_OPTIONS}
      />

      <Flex sx={{ gap: 8 }}>
        <Flex
          sx={{
            flexDirection: 'column',
            gap: 9,
            paddingLeft: 6,
            fontSize: 2,
            position: 'relative',
          }}
        >
          <Flex
            sx={{
              position: 'absolute',
              zIndex: -1,
              width: '100%',
              height: '83vh',
              top: 0,
              left: 0,
            }}
            className="modal-layers-list"
          />
          {layers.map((layer, idx) => {
            return (
              <Flex
                key={`layer-${layer.group}-${layer.code}-${idx}`}
                sx={{ flexDirection: 'column' }}
              >
                <LayerGroup
                  onSelect={(layerCode) =>
                    setSelected({ groupCode: layer.code, layerCode })
                  }
                  selected={selected}
                  key={`${layer.group}-${idx}`}
                  layer={layer}
                />
              </Flex>
            );
          })}
        </Flex>
      </Flex>

      <Box
        className="modal-layers-button-apply"
        sx={{ position: 'sticky', bottom: 0, backgroundColor: 'background' }}
      >
        <HorizontalLine sx={{ marginTop: 9 }} />

        <Flex
          sx={{
            justifyContent: 'space-evenly',
            paddingY: 7,
          }}
        >
          <Button
            onClick={() => selected && onApply(selected)}
            sx={{ minWidth: '119px' }}
            size="md"
            outlined={!selected}
          >
            Aplicar
          </Button>

          <Button
            onClick={onCancel}
            sx={{ minWidth: '119px' }}
            size="md"
            outlined
          >
            Cancelar
          </Button>
        </Flex>

        <HorizontalLine sx={{ marginTop: 1 }} />
      </Box>
    </Box>
  );
};

export default LayerCategory;
